/*
========================================
PRODUCT PREVIEW

BLOCK: SIDE PANELS

Отвечает за:
- левую шторку
- правую шторку
- стрелки открытия
- свайпы
- запрет открытия двух панелей одновременно
- весь контент внутри панелей

Редактируется симметрично.
========================================
*/

import { clx } from "@medusajs/ui"

type ProductPreviewSidePanelsProps = {
  leftPanelOpen: boolean
  rightPanelOpen: boolean
  toggleLeftPanel: () => void
  toggleRightPanel: () => void
  handleTouchStart: (e: React.TouchEvent) => void
  handleTouchEnd: (e: React.TouchEvent) => void
  title?: string | null
  description?: string | null
  sku?: string | null
  text: any
  HANDLE_WIDTH: number
}

export default function ProductPreviewSidePanels({
  leftPanelOpen,
  rightPanelOpen,
  toggleLeftPanel,
  toggleRightPanel,
  handleTouchStart,
  handleTouchEnd,
  title,
  description,
  sku,
  text,
  HANDLE_WIDTH,
}: ProductPreviewSidePanelsProps) {
  return (
    <>
      {/* ========================================
          LEFT PANEL
      ======================================== */}
      <div
        className={clx(
          "pointer-events-none absolute left-0 top-0 h-full w-full transition-transform duration-300",
          leftPanelOpen ? "z-30" : "z-20"
        )}
        style={{
          transform: leftPanelOpen
            ? "translateX(0)"
            : `translateX(calc(-100% + ${HANDLE_WIDTH}px))`,
          animation: !leftPanelOpen ? "panelNudgeLeft 1.8s ease-in-out infinite" : "none",
        }}
      >
        <div
          className={clx(
            "flex h-full min-w-full w-full items-stretch rounded-r-2xl transition-all duration-300",
            leftPanelOpen
              ? "border border-black/10 bg-transparent shadow-lg backdrop-blur-md"
              : "border-transparent bg-transparent shadow-none backdrop-blur-0"
          )}
        >
          <div
            className={clx(
              "no-scrollbar h-full flex-1 overflow-y-auto overscroll-contain p-0 text-left touch-pan-y transition-opacity duration-200",
              "pointer-events-auto",
              !leftPanelOpen && "opacity-0",
              rightPanelOpen && "pointer-events-none"
            )}
            style={{ WebkitOverflowScrolling: "touch" }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="min-h-full rounded-r-2xl">
              <div className="m-2 rounded-r-2xl border border-black/10 bg-white/50 px-4 py-4 shadow-sm backdrop-blur-sm">
                <div className="text-base font-bold leading-5 text-black">
                  {title || "Product"}
                </div>

                <div className="mt-3 whitespace-pre-line text-sm leading-5 text-black/80">
                  {description?.trim() || "No description available."}
                </div>

                <div className="mt-4 border-t border-black/10 pt-3 text-sm font-semibold text-black/80">
                  SKU: {sku || "—"}
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={toggleLeftPanel}
            disabled={rightPanelOpen}
            className={clx(
              "pointer-events-auto relative flex h-full w-12 shrink-0 items-center justify-center",
              "bg-transparent",
              rightPanelOpen && "pointer-events-none opacity-40"
            )}
            aria-label="Toggle left panel"
          >
            <span
              className={clx(
                "flex h-[20%] w-full items-center justify-center",
                "border border-orange-500 bg-white/95",
                leftPanelOpen ? "rounded-l-2xl" : "rounded-r-2xl",
                "text-xl font-bold text-orange-500 transition-all duration-300"
              )}
            >
              {leftPanelOpen ? "‹" : "›"}
            </span>
          </button>
        </div>
      </div>

      {/* ========================================
          RIGHT PANEL
      ======================================== */}
      <div
        className={clx(
          "pointer-events-none absolute right-0 top-0 h-full w-full transition-transform duration-300",
          rightPanelOpen ? "z-30" : "z-20"
        )}
        style={{
          transform: rightPanelOpen
            ? "translateX(0)"
            : `translateX(calc(100% - ${HANDLE_WIDTH}px))`,
          animation: !rightPanelOpen ? "panelNudgeRight 1.8s ease-in-out infinite" : "none",
        }}
      >
        <div
          className={clx(
            "flex h-full min-w-full w-full items-stretch rounded-l-2xl transition-all duration-300",
            rightPanelOpen
              ? "border border-black/10 bg-transparent shadow-lg backdrop-blur-md"
              : "border-transparent bg-transparent shadow-none backdrop-blur-0"
          )}
        >
          <button
            type="button"
            onClick={toggleRightPanel}
            disabled={leftPanelOpen}
            className={clx(
              "pointer-events-auto relative flex h-full w-12 shrink-0 items-center justify-center",
              "bg-transparent",
              leftPanelOpen && "pointer-events-none opacity-40"
            )}
            aria-label="Toggle right panel"
          >
            <span
              className={clx(
                "flex h-[20%] w-full items-center justify-center",
                "border border-orange-500 bg-white/95",
                rightPanelOpen ? "rounded-r-2xl" : "rounded-l-2xl",
                "text-xl font-bold text-orange-500 transition-all duration-300"
              )}
            >
              {rightPanelOpen ? "›" : "‹"}
            </span>
          </button>

          <div
            className={clx(
              "no-scrollbar h-full flex-1 overflow-y-auto overscroll-contain p-0 text-left touch-pan-y transition-opacity duration-200",
              "pointer-events-auto",
              !rightPanelOpen && "opacity-0",
              leftPanelOpen && "pointer-events-none"
            )}
            style={{ WebkitOverflowScrolling: "touch" }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="min-h-full rounded-l-2xl">
              <div className="m-2 rounded-l-2xl border border-black/10 bg-white/50 px-4 py-4 shadow-sm backdrop-blur-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-white/70 px-3 py-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-black/55">
                      ❤️ {text.want}
                    </div>
                    <div className="mt-1 text-lg font-extrabold leading-none text-black">
                      128
                    </div>
                  </div>

                  <div className="rounded-xl bg-white/70 px-3 py-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-black/55">
                      ✔ {text.have}
                    </div>
                    <div className="mt-1 text-lg font-extrabold leading-none text-black">
                      64
                    </div>
                  </div>

                  <div className="rounded-xl bg-white/70 px-3 py-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-black/55">
                      ⭐ {text.recommend}
                    </div>
                    <div className="mt-1 text-lg font-extrabold leading-none text-black">
                      29
                    </div>
                  </div>

                  <div className="rounded-xl bg-white/70 px-3 py-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-black/55">
                      💬 {text.comments}
                    </div>
                    <div className="mt-1 text-lg font-extrabold leading-none text-black">
                      12
                    </div>
                  </div>
                </div>

                <div className="mt-3 rounded-xl border border-black/10 bg-gradient-to-r from-orange-100/80 to-amber-50/80 px-3 py-3">
                  <div className="text-xs font-semibold text-black/60">
                    {text.pointsTitle}
                  </div>
                  <div className="mt-1 text-base font-extrabold text-black">
                    {text.pointsValue}
                  </div>
                  <div className="mt-1 text-xs text-black/65">
                    {text.pointsHint}
                  </div>
                </div>

                <div className="mt-3 rounded-xl bg-white/70 px-3 py-3">
                  <div className="text-sm font-bold text-black">
                    {text.joinTitle}
                  </div>
                  <div className="mt-1 text-xs leading-4 text-black/70">
                    {text.joinText}
                  </div>

                  <div className="mt-3 inline-flex w-full items-center justify-center rounded-xl bg-orange-500 px-3 py-2 text-sm font-bold text-white">
                    {text.joinButton}
                  </div>
                </div>

                <div className="mt-3 rounded-xl bg-white/70 px-3 py-3">
                  <div className="text-sm font-bold text-black">
                    {text.comments}
                  </div>

                  <div className="mt-2 rounded-lg bg-white/80 px-3 py-2 text-xs leading-4 text-black/75">
                    {text.comment1}
                  </div>

                  <div className="mt-2 rounded-lg bg-white/80 px-3 py-2 text-xs leading-4 text-black/75">
                    {text.comment2}
                  </div>

                  <div className="mt-2 rounded-lg bg-white/80 px-3 py-2 text-xs leading-4 text-black/75">
                    This product looks interesting, I would like to try it later.
                  </div>

                  <div className="mt-2 rounded-lg bg-white/80 px-3 py-2 text-xs leading-4 text-black/75">
                    I already have a similar item and can recommend this type.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}